import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Share2, Gift, Users, Wallet, Award } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ReferralSection = () => {
  const { getAuthHeader } = useAuth();
  const [referralData, setReferralData] = useState(null);
  const [loyaltyData, setLoyaltyData] = useState(null);
  const [applyCode, setApplyCode] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = getAuthHeader();
      const [refRes, loyaltyRes] = await Promise.all([
        axios.get(`${API}/referral/my-code`, { headers }),
        axios.get(`${API}/loyalty/points`, { headers })
      ]);
      setReferralData(refRes.data);
      setLoyaltyData(loyaltyRes.data);
    } catch (error) {
      console.error(error);
    }
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(referralData.referral_code);
    toast.success('Referral code copied!');
  };

  const handleApplyCode = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/referral/apply?referral_code=${applyCode}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Referral code applied! ₹50 added to your wallet');
      setApplyCode('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply code');
    }
  };

  const handleRedeemPoints = async (points) => {
    try {
      const response = await axios.post(
        `${API}/loyalty/redeem?points=${points}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Redemption failed');
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Referral Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Share2 className="h-5 w-5 text-primary" />
            <span>Refer & Earn</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {referralData && (
            <>
              <div className="bg-primary/5 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-2">Your Referral Code</div>
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold font-mono text-primary">{referralData.referral_code}</div>
                  <Button size="sm" onClick={copyReferralCode} data-testid="copy-referral-code">
                    Copy
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="flex items-center justify-center mb-2">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                  <div className="text-2xl font-bold text-primary">{referralData.total_referrals}</div>
                  <div className="text-xs text-gray-600">Referrals</div>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="flex items-center justify-center mb-2">
                    <Wallet className="h-5 w-5 text-primary" />
                  </div>
                  <div className="text-2xl font-bold text-primary">₹{referralData.total_earnings}</div>
                  <div className="text-xs text-gray-600">Earned</div>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="text-sm font-semibold mb-2">Have a referral code?</div>
                <form onSubmit={handleApplyCode} className="flex space-x-2">
                  <Input
                    placeholder="Enter code"
                    value={applyCode}
                    onChange={(e) => setApplyCode(e.target.value.toUpperCase())}
                    data-testid="apply-referral-input"
                  />
                  <Button type="submit" data-testid="apply-referral-btn">Apply</Button>
                </form>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Loyalty Points Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Award className="h-5 w-5 text-primary" />
            <span>Loyalty Points</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {loyaltyData && (
            <>
              <div className="text-center p-6 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg">
                <div className="text-4xl font-bold text-primary mb-2">{loyaltyData.total_points}</div>
                <div className="text-sm text-gray-600">Total Points</div>
              </div>

              <div className="space-y-2">
                <div className="text-sm font-semibold mb-2">Redeem Rewards</div>
                {loyaltyData.rewards.map((reward, i) => (
                  <div key={i} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <div className="font-medium text-sm">{reward.reward}</div>
                      <div className="text-xs text-gray-600">{reward.points} points</div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleRedeemPoints(reward.points)}
                      disabled={loyaltyData.total_points < reward.points}
                      data-testid={`redeem-${reward.points}`}
                    >
                      Redeem
                    </Button>
                  </div>
                ))}
              </div>

              <div className="text-xs text-gray-500 text-center pt-2">
                Earn points with every purchase and referral
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReferralSection;
